import React from 'react';
import ProtectedRoute from '../../@crema/core/ProtectedRoute';
import PortalRoute from '../../@crema/core/PortalRoute';
import {RoutePermittedRole} from '../../shared/constants/AppConst';
import {usePermissions} from '../../hooks/usePermissions';

// Pending Agreement Page (accessible to inactive agreement-pending users)
const PendingAgreementPage = React.lazy(() => import('./PendingAgreement'));

// WhatsApp Service Elements (Shared)
const MuraasalaConnection = React.lazy(() => import('./Connection'));
const MuraasalaSendMessage = React.lazy(() => import('./SendMessage'));
const MuraasalaSendMultiple = React.lazy(() => import('./SendMultiple'));
const MuraasalaBulkMessages = React.lazy(() => import('./BulkMessages'));
const MuraasalaMessageHistory = React.lazy(() => import('./MessageHistory'));
const MuraasalaReports = React.lazy(() => import('./Reports'));
const MuraasalaSettings = React.lazy(() => import('./Settings'));
const MuraasalaContacts = React.lazy(() => import('./Contacts'));
const MuraasalaContactGroups = React.lazy(() => import('./ContactGroups'));
const MuraasalaContactsImport = React.lazy(() => import('./ContactsImport'));
const MuraasalaScheduledCampaigns = React.lazy(() =>
  import('./ScheduledCampaigns'),
);
const MuraasalaPolls = React.lazy(() => import('./Polls'));
const MuraasalaGroupMessages = React.lazy(() => import('./GroupMessages'));
const MuraasalaWhatsAppGroups = React.lazy(() => import('./WhatsAppGroups'));
const MuraasalaAnalytics = React.lazy(() => import('./Analytics'));
const MuraasalaTemplates = React.lazy(() => import('./Templates'));
const MuraasalaSystemMonitor = React.lazy(() => import('./SystemMonitor'));
const MuraasalaConversations = React.lazy(() => import('./Conversations'));
const MuraasalaAutoReply = React.lazy(() => import('./AutoReply'));
const MuraasalaWebhooks = React.lazy(() => import('./Webhooks'));

// Portal Users
const PortalUsersManagement = React.lazy(() => import('./PortalUsers'));

// Superadmin Pages (v3.2.0)
const SuperAdminPlatformOverview = React.lazy(() =>
  import('./SuperAdmin/PlatformOverview'),
);
const AdminOverview = React.lazy(() => import('./SuperAdmin/AdminOverview'));
const SuperAdminAccountOverview = React.lazy(() =>
  import('./SuperAdmin/AccountOverview'),
);

// /platform/overview landing, rendered per role: the superadmin sees the full
// platform overview (user CRUD, plans, aggregate stats); the admin — a management
// role with no api_key — sees a keyless Admin Console of the tools it is actually
// authorized for. Both share one URL so RoleBasedRedirect need not special-case.
const PlatformLanding = () => {
  const {isAdmin} = usePermissions();
  return isAdmin ? <AdminOverview /> : <SuperAdminPlatformOverview />;
};
const SuperAdminUsers = React.lazy(() => import('./SuperAdmin/Users'));
const SuperAdminSendOnBehalf = React.lazy(() =>
  import('./SuperAdmin/SendOnBehalf'),
);
const SuperAdminSendThrottle = React.lazy(() =>
  import('./SuperAdmin/SendThrottle'),
);
const SuperAdminThrottleMonitor = React.lazy(() =>
  import('./SuperAdmin/ThrottleMonitor'),
);
const SuperAdminPlans = React.lazy(() => import('./SuperAdmin/Plans'));
const SuperAdminSmtpSettings = React.lazy(() =>
  import('./SuperAdmin/SmtpSettings'),
);
const SuperAdminProviderView = React.lazy(() =>
  import('./SuperAdmin/ProviderView'),
);
const SuperAdminBalanceMonitor = React.lazy(() =>
  import('./SuperAdmin/BalanceMonitor'),
);
const SuperAdminFinancial = React.lazy(() => import('./SuperAdmin/Financial'));
const SuperAdminExpenses = React.lazy(() => import('./SuperAdmin/Expenses'));
const SuperAdminPendingApprovals = React.lazy(() =>
  import('./SuperAdmin/PendingApprovals'),
);
const SuperAdminSystemLogs = React.lazy(() =>
  import('./SuperAdmin/SystemLogs'),
);
const SuperAdminPlatformMonitor = React.lazy(() =>
  import('./SuperAdmin/PlatformMonitor'),
);

// Provider Pages (v3.2.0)
const ProviderAccountOverview = React.lazy(() =>
  import('./Provider/AccountOverview'),
);
const ProviderDashboard = React.lazy(() => import('./Provider/Dashboard'));
const ProviderServiceUsers = React.lazy(() =>
  import('./Provider/ServiceUsers'),
);
const ProviderServiceUserDetail = React.lazy(() =>
  import('./Provider/ServiceUserDetail'),
);

// User Pages (v3.2.0)
const UserDashboard = React.lazy(() => import('./User/Dashboard'));

// Guide Pages
const UserGuide = React.lazy(() => import('./Guides/UserGuide'));
const ProviderGuide = React.lazy(() => import('./Guides/ProviderGuide'));

/**
 * Muraasala (WhatsApp Messaging Service) Route Configuration v3.2.0
 *
 * Using permittedRole at route level for RouteGenerator compatibility
 * AND ProtectedRoute for additional security checks
 */
export const muraasalaRoutesConfig = [
  // ==================== PENDING AGREEMENT (accessible without is_active check) ====================
  {
    path: '/pending-agreement',
    element: <PendingAgreementPage />,
    permittedRole: RoutePermittedRole.user,
  },

  // ==================== FALLBACK ====================
  {
    path: '/muraasala',
    element: (
      <ProtectedRoute>
        <UserDashboard />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },

  // ==================== SUPERADMIN PORTAL ====================
  {
    path: '/platform/overview',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
        <PlatformLanding />
      </ProtectedRoute>
    ),
    permittedRole: ['superadmin', 'admin'],
  },
  {
    path: '/platform/account',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminAccountOverview />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/users',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminUsers />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/send-on-behalf',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
        <SuperAdminSendOnBehalf />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.admin,
  },
  {
    path: '/platform/send-throttle',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
        <SuperAdminSendThrottle />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.admin,
  },
  {
    path: '/platform/throttle-monitor',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
        <SuperAdminThrottleMonitor />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.admin,
  },
  {
    path: '/platform/plans',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminPlans />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/users/:userId',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'provider']}>
        <ProviderServiceUserDetail />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/provider/:providerId',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminProviderView />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/smtp-settings',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'admin']}>
        <SuperAdminSmtpSettings />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.admin,
  },
  {
    path: '/platform/balance-monitor',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'provider']}>
        <SuperAdminBalanceMonitor />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/financial',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminFinancial />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/expenses',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminExpenses />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/pending-approvals',
    element: (
      <ProtectedRoute requiredRoles={['superadmin', 'provider']}>
        <SuperAdminPendingApprovals />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    path: '/platform/system-logs',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminSystemLogs />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  {
    // Platform Monitor proxies through proxyApi, which requires an api_key the
    // (keyless) admin role does not hold -> superadmin only.
    path: '/platform/monitor',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <SuperAdminPlatformMonitor />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },
  // ==================== PROVIDER PORTAL ====================
  {
    path: '/provider/overview',
    element: (
      <ProtectedRoute requiredRoles={['provider']}>
        <ProviderAccountOverview />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.provider,
  },
  {
    path: '/provider/service-users',
    element: (
      <ProtectedRoute requiredRoles={['provider']}>
        <ProviderServiceUsers />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.provider,
  },
  {
    path: '/provider/service-users/:userId',
    element: (
      <ProtectedRoute requiredRoles={['provider', 'superadmin']}>
        <ProviderServiceUserDetail />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.provider,
  },
  // Legacy routes
  {
    path: '/provider/dashboard',
    element: (
      <ProtectedRoute requiredRoles={['provider']}>
        <ProviderDashboard />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.provider,
  },
  {
    path: '/provider/users',
    element: (
      <ProtectedRoute requiredRoles={['provider']}>
        <ProviderServiceUsers />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.provider,
  },

  // ==================== USER PORTAL ====================
  {
    path: '/user/overview',
    element: (
      <ProtectedRoute>
        <UserDashboard />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/user/dashboard',
    element: (
      <ProtectedRoute>
        <UserDashboard />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },

  // ==================== PORTAL USERS ====================
  {
    path: '/portal-users',
    element: (
      <ProtectedRoute>
        <PortalUsersManagement />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/provider/portal-users',
    element: (
      <ProtectedRoute requiredRoles={['provider']}>
        <PortalUsersManagement />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.provider,
  },
  {
    path: '/platform/portal-users',
    element: (
      <ProtectedRoute requiredRoles={['superadmin']}>
        <PortalUsersManagement />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.superadmin,
  },

  // ==================== SHARED SERVICE ROUTES ====================
  {
    path: '/service/connection',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.connection'>
          <MuraasalaConnection />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/send',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.send'>
          <MuraasalaSendMessage />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/conversations',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.conversations'>
          <MuraasalaConversations />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    // No PortalRoute: the webhook API is scoped to req.user.id, so a portal
    // user would be managing their OWN webhooks, not the tenant's. This is an
    // account-owner screen.
    path: '/service/webhooks',
    element: (
      <ProtectedRoute>
        <MuraasalaWebhooks />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/auto-reply',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.autoReply'>
          <MuraasalaAutoReply />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/send-multiple',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.send'>
          <MuraasalaSendMultiple />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/bulk',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.bulk'>
          <MuraasalaBulkMessages />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/history',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.history'>
          <MuraasalaMessageHistory />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/contacts',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.contacts'>
          <MuraasalaContacts />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/contact-groups',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.contacts'>
          <MuraasalaContactGroups />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/contacts/import',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.contacts'>
          <MuraasalaContactsImport />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/scheduled-campaigns',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.send'>
          <MuraasalaScheduledCampaigns />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/polls',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.bulk'>
          <MuraasalaPolls />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/group-messages',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.send'>
          <MuraasalaGroupMessages />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/groups',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.contacts'>
          <MuraasalaWhatsAppGroups />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/analytics',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.analytics'>
          <MuraasalaAnalytics />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/templates',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.templates'>
          <MuraasalaTemplates />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/reports',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.reports'>
          <MuraasalaReports />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/service/monitor',
    element: (
      <ProtectedRoute>
        <PortalRoute pageKey='service.monitor'>
          <MuraasalaSystemMonitor />
        </PortalRoute>
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/account/settings',
    element: (
      <ProtectedRoute>
        <MuraasalaSettings />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },

  // ==================== GUIDES ====================
  {
    path: '/service/guide',
    element: (
      <ProtectedRoute>
        <UserGuide />
      </ProtectedRoute>
    ),
    permittedRole: RoutePermittedRole.user,
  },
  {
    path: '/provider/guide',
    element: (
      <ProtectedRoute requiredRoles={['provider', 'superadmin']}>
        <ProviderGuide />
      </ProtectedRoute>
    ),
    permittedRole: ['provider', 'superadmin'],
  },
];

export default muraasalaRoutesConfig;
